import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { fetchCustomerOrders } from "features/customers/services/customersService";
import { fetchOrderById } from "features/drivers/services/driversService";

function OrdersHistory({ currentId, title = "Recent Orders" }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [orders, setOrders] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const page = currentPage;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const fetchOrders = useCallback(
    async (page) => {
      if (!currentId) {
        setOrders([]);
        setTotalPages(1);
        setTotalItems(0);
        return;
      }

      try {
        const response = await fetchCustomerOrders(currentId, { page, total: totalItems });
        if (response?.success) {
          const { items, pagination } = response.data || {};
          setOrders(items || []);
          if (pagination) {
            setTotalPages(pagination.total_pages || 1);
            setCurrentPage(pagination.current_page || page || 1);
            if (typeof pagination.totalItems === "number") setTotalItems(pagination.totalItems);
          }
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error("Error fetching customer orders:", error);
      }
    },
    [currentId, totalItems]
  );

  const fetchSingleOrder = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const response = await fetchOrderById(id);
      if (response?.success) {
        setSelectedOrder(response.data);
        setDetailOpen(true);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const handleView = (order) => {
    if (order?.id) fetchSingleOrder(order.id);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedOrder(null);
  };

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentId, currentPage, fetchOrders]);

  return (
    <>
      <Card>
        <CardHeader title={title} />
        <CardContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Order Ref</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell>{o.order_ref || o.id}</TableCell>
                      <TableCell>
                        {o.createdAt || o.date ? new Date(o.createdAt || o.date).toLocaleString() : ""}
                      </TableCell>
                      <TableCell style={{ textTransform: "capitalize" }}>
                        {o.current_order_status?.name || o.status || "-"}
                      </TableCell>
                      <TableCell align="right">QAR{Number(o.total_amount ?? o.total ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Button size="small" style={{ color: "gray" }} variant="outlined" onClick={() => handleView(o)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
            <Button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
              Previous
            </Button>
            <MDTypography mx={2}>Page {page} of {totalPages}</MDTypography>
            <Button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
              Next
            </Button>
          </MDBox>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent dividers>
          {loadingDetail && <Typography variant="body2">Loading...</Typography>}
          {!loadingDetail && selectedOrder && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">Summary</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2"><strong>Order Ref:</strong> {selectedOrder.order_ref}</Typography>
                  <Typography variant="body2"><strong>Status:</strong> {selectedOrder.current_order_status?.name || selectedOrder.current_status_id}</Typography>
                  <Typography variant="body2"><strong>Type:</strong> {selectedOrder.order_type}</Typography>
                  <Typography variant="body2"><strong>Payment:</strong> {selectedOrder.payment_method} / {selectedOrder.payment_status}</Typography>
                  <Typography variant="body2"><strong>Total:</strong> QAR {selectedOrder.total_amount}</Typography>
                  <Typography variant="body2"><strong>Net:</strong> QAR {selectedOrder.net_amount}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1">Addresses</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" style={{ wordBreak: 'break-word' }}><strong>Pickup:</strong> {selectedOrder.pickup_address}</Typography>
                  <Typography variant="body2" style={{ wordBreak: 'break-word' }}><strong>Dropoff:</strong> {selectedOrder.dropoff_address}</Typography>
                  <Typography variant="body2"><strong>Distance:</strong> {selectedOrder.distance} km</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Items</Typography>
                  <Divider sx={{ my: 1 }} />
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Service</strong></TableCell>
                          <TableCell><strong>Value</strong></TableCell>
                          <TableCell align="right"><strong>Price</strong></TableCell>
                        </TableRow>
                        {selectedOrder.details?.length ? selectedOrder.details.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.order_option.name_en}</TableCell>
                            <TableCell>{item.value}</TableCell>
                            <TableCell align="right">QAR{item.price}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No items</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Trackings</Typography>
                  <Divider sx={{ my: 1 }} />
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                        </TableRow>
                        {selectedOrder.trackings?.length ? selectedOrder.trackings.map((tracking) => (
                          <TableRow key={tracking.id}>
                            <TableCell>{tracking.order_status?.name || '-'}</TableCell>
                            <TableCell>{tracking.description || '-'}</TableCell>
                            <TableCell>{tracking.createdAt ? new Date(tracking.createdAt).toLocaleString() : '-'}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No tracking history</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OrdersHistory;
